import * as React from "react"
import { ChevronDown, Plus, X } from "lucide-react"
import { cn } from "../lib/utils"
import Button from "./Button"

interface SelectProps {
  id?: string
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  children?: React.ReactNode
}

interface SelectItemProps {
  value: string
  children: React.ReactNode
  className?: string
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ id, value, onValueChange, placeholder, disabled, className, children }, ref) => {
    return (
      <div className="relative">
        <select
          id={id}
          ref={ref}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none cursor-pointer pr-10",
            className
          )}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      </div>
    )
  }
)
Select.displayName = "Select"

const SelectItem: React.FC<SelectItemProps> = ({ value, children, className }) => {
  return (
    <option value={value} className={className}>
      {children}
    </option>
  )
}

// Enhanced select with custom value support
interface CustomSelectProps {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder?: string
  disabled?: boolean
  required?: boolean
  error?: string
  onAddCustom?: (value: string) => void
  showAddCustom?: boolean
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  required,
  error,
  onAddCustom,
  showAddCustom = true
}) => {
  const [isAddingCustom, setIsAddingCustom] = React.useState(false)
  const [customValue, setCustomValue] = React.useState('')

  const handleSelectChange = (newValue: string) => {
    if (newValue === '__custom__') {
      setIsAddingCustom(true)
    } else {
      onChange(newValue)
    }
  }

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      const trimmedValue = customValue.trim()
      if (onAddCustom) {
        onAddCustom(trimmedValue)
      }
      onChange(trimmedValue)
      setCustomValue('')
      setIsAddingCustom(false)
    }
  }

  const handleCancel = () => {
    setIsAddingCustom(false)
    setCustomValue('')
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
        {showAddCustom && !isAddingCustom && (
          <button
            type="button"
            onClick={() => setIsAddingCustom(true)}
            disabled={disabled}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-3 w-3" />
            Add Custom
          </button>
        )}
      </div>

      {isAddingCustom ? (
        <div className="space-y-2">
          <div className="relative">
            <input
              type="text"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pr-20"
              placeholder="Enter custom value"
              disabled={disabled}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleCustomSubmit()
                } else if (e.key === 'Escape') {
                  handleCancel()
                }
              }}
            />
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex gap-1">
              <Button
                type="button"
                size="sm"
                variant="default"
                onClick={handleCustomSubmit}
                disabled={disabled || !customValue.trim()}
                className="h-7 px-2 text-xs"
              >
                Add
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                disabled={disabled}
                className="h-7 w-7 p-0"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <Select
          id={id}
          value={value}
          onValueChange={handleSelectChange}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(error && "border-destructive focus:ring-destructive")}
        >
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
          {showAddCustom && (
            <SelectItem value="__custom__" className="text-primary font-medium">
              + Add Custom...
            </SelectItem>
          )}
        </Select>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

export { Select, SelectItem, CustomSelect }
